import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { FRONTEND_FEATURES } from './AdminDashboard';
import { FeatureInfo } from '../components/Misc/FeatureInfo';
import { cacheManager } from '../utils/CacheManager';
import {
  clearStoredCode,
  readStoredCode,
  writeStoredCode,
} from './replay/ReplayAccessGate';
const TOTAL_MULTIPLIER = 1000000;
const STORAGE_KEY = 'devMasterDashboard.columns';
const PIN_STORAGE_KEY = 'devMasterDashboard.pinned';

const buildDefaultRange = () => {
  const end = new Date();
  end.setDate(end.getDate() + 1); // pad end date by a day to be inclusive
  const start = new Date();
  start.setDate(start.getDate() - 30); // last 30 days by default
  return { t1: end, t2: start };
};

const formatCost = (raw) =>
  typeof raw === 'number'
    ? (raw / TOTAL_MULTIPLIER).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '-';

const formatHours = (seconds) =>
  typeof seconds === 'number'
    ? (seconds / 3600).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '-';

const flattenUserData = (value, prefix = '', result = {}, depth = 0, maxDepth = 3) => {
  if (depth > maxDepth) return result;
  if (Array.isArray(value)) {
    value.forEach((item, idx) => {
      const nextPrefix = prefix ? `${prefix}[${idx}]` : `[${idx}]`;
      if (item && typeof item === 'object') {
        flattenUserData(item, nextPrefix, result, depth + 1, maxDepth);
      } else {
        result[nextPrefix] = item;
      }
    });
  } else if (value && typeof value === 'object') {
    Object.entries(value).forEach(([k, v]) => {
      const nextPrefix = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object') {
        flattenUserData(v, nextPrefix, result, depth + 1, maxDepth);
      } else {
        result[nextPrefix] = v;
      }
    });
  } else if (prefix) {
    result[prefix] = value;
  }
  return result;
};

const parseJsonIfString = (val) => {
  if (typeof val !== 'string') return val;
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
};

const DevMasterDashboard = ({ apiClient }) => {
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [results, setResults] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortState, setSortState] = useState({ key: 'email', dir: 'asc' });
  const [columnSearch, setColumnSearch] = useState('');
  const [pinnedFields, setPinnedFields] = useState([]);

  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});

  const [groupUsage, setGroupUsage] = useState({
    isOpen: false,
    loading: false,
    data: null,
    groupId: null,
    error: null,
    range: null,
  });
  const [groupSort, setGroupSort] = useState({ key: 'cost', dir: 'desc' });
  const [actionModal, setActionModal] = useState({
    open: false,
    user: null,
    action: null,
  });
  const [actionForm, setActionForm] = useState({
    expirationDate: new Date(),
    mfaCode: '',
    replayToken: null,
    submitting: false,
    status: null,
  });
  const [openActionRow, setOpenActionRow] = useState(null);
  const [actionFeatures, setActionFeatures] = useState({
    backend: {},
    user: {},
    combined: {},
    isLoading: false,
    status: null,
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchKey.trim()) {
      setSearchError('Enter a name or email to search.');
      return;
    }
    setSearchError('');
    setSearching(true);
    try {
      const rsp = await apiClient.adminSearchUser(searchKey.trim());
      const normalized = Array.isArray(rsp)
        ? rsp.map((u) => {
            const pd = parseJsonIfString(u?.professional_details);
            const merged = pd && typeof pd === 'object' ? { ...u, professional_details: pd } : u;
            const flattened = flattenUserData(merged) || {};
            return {
              ...merged,
              ...flattened,
            };
          })
        : [];
      setResults(normalized);
      setExpanded({});
      setDetails({});
      // prime column visibility after results land
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) setVisibleColumns(parsed);
        } catch (e) {
          console.warn('Bad column prefs', e);
        }
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchError('Unable to search users right now.');
    } finally {
      setSearching(false);
    }
  };

  const allKeys = useMemo(() => {
    const keys = new Set();
    results.forEach((u) => Object.keys(u || {}).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [results]);

  // initialize visible columns once we know the keys
  useEffect(() => {
    if (visibleColumns.length === 0 && allKeys.length > 0) {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setVisibleColumns(parsed.filter((k) => allKeys.includes(k)));
            return;
          }
        } catch (e) {
          console.warn('Bad column prefs', e);
        }
      }
      const baseDefaults = [
        'name',
        'email',
        'expiration',
        'group_subscription_id',
        'credit_usage',
      ];
      const profDefaults = allKeys.filter((k) =>
        k.startsWith('professional_details.'),
      );
      const defaults = [...baseDefaults, ...profDefaults].filter((k) =>
        allKeys.includes(k),
      );
      setVisibleColumns(defaults.length ? defaults : allKeys.slice(0, 5));
    }
  }, [allKeys, visibleColumns.length]);

  useEffect(() => {
    const storedPins = window.localStorage.getItem(PIN_STORAGE_KEY);
    if (storedPins) {
      try {
        const parsed = JSON.parse(storedPins);
        if (Array.isArray(parsed)) setPinnedFields(parsed);
      } catch (e) {
        console.warn('Bad pin prefs', e);
      }
    }
  }, []);

  const persistColumns = (cols) => {
    setVisibleColumns(cols);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
    } catch (e) {
      console.warn('Unable to persist column prefs', e);
    }
  };

  const persistPins = (pins) => {
    setPinnedFields(pins);
    try {
      window.localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(pins));
    } catch (e) {
      console.warn('Unable to persist pin prefs', e);
    }
  };

  const moveColumn = (key, direction) => {
    const idx = visibleColumns.indexOf(key);
    if (idx === -1) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= visibleColumns.length) return;
    const next = [...visibleColumns];
    [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
    persistColumns(next);
  };

  const toggleColumn = (key) => {
    if (!key) return;
    if (visibleColumns.includes(key)) {
      const next = visibleColumns.filter((c) => c !== key);
      persistColumns(next);
    } else {
      persistColumns([...visibleColumns, key]);
    }
  };

  const formatDateShort = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const displayValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') {
      const friendly = formatDateShort(val);
      if (friendly) return friendly;
      return val;
    }
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const renderUserFields = (user) => {
    if (!user) return null;
    const entries = Object.keys(user).sort();
    return (
      <details style={{ marginTop: '0.5rem' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          Show all user fields
        </summary>
        <div
          style={{
            marginTop: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <div style={{ fontWeight: 600 }}>Pins</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <Input
              bsSize="sm"
              type="select"
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                if (!pinnedFields.includes(val)) {
                  persistPins([...pinnedFields, val]);
                }
                e.target.value = '';
              }}
              style={{ minWidth: '180px' }}
            >
              <option value="">Pin a field…</option>
              {Object.keys(user)
                .filter((k) => !pinnedFields.includes(k))
                .sort()
                .map((k) => (
                  <option key={`pin-opt-${k}`} value={k}>
                    {k}
                  </option>
                ))}
            </Input>
            <Button size="sm" color="secondary" onClick={() => persistPins([])}>
              Clear pins
            </Button>
          </div>
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <Table size="sm" responsive style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ width: '35%' }}>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((key) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{displayValue(user[key])}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </details>
    );
  };

  const renderPinnedFields = (user) => {
    if (!user || pinnedFields.length === 0) return null;
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 12px',
          marginBottom: '0.5rem',
          fontSize: '0.9rem',
        }}
      >
        {pinnedFields.map((field) => (
          <div
            key={field}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '6px 8px',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '2px' }}>{field}</div>
            <div>{displayValue(user[field]) || '-'}</div>
          </div>
        ))}
      </div>
    );
  };

  const sortedResults = useMemo(() => {
    if (!sortState.key) return results;
    const dir = sortState.dir === 'desc' ? -1 : 1;
    const key = sortState.key;
    const safeVal = (v) => {
      const raw = v?.[key];
      if (raw === null || raw === undefined) return '';
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'boolean') return raw ? 1 : 0;
      return String(raw).toLowerCase();
    };
    return [...results].sort((a, b) => {
      const va = safeVal(a);
      const vb = safeVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [results, sortState]);

  const orderedKeys = useMemo(() => {
    const selected = visibleColumns.filter((k) => allKeys.includes(k));
    const remaining = allKeys.filter((k) => !visibleColumns.includes(k));
    return [...selected, ...remaining];
  }, [allKeys, visibleColumns]);

  const filteredKeys = useMemo(() => {
    const source = orderedKeys;
    if (!columnSearch.trim()) return source;
    const term = columnSearch.toLowerCase();
    return source.filter((k) => k.toLowerCase().includes(term));
  }, [orderedKeys, columnSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && groupUsage.isOpen) {
        closeGroupUsage();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [groupUsage.isOpen]);

  const modalSizingStyles = `
    .group-overlay {
      position: fixed;
      inset: 0;
      z-index: 1050;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .group-overlay__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.5);
      pointer-events: auto;
    }
    .group-overlay__panel {
      position: relative;
      background: #fff;
      width: 96vw;
      max-width: 1400px;
      height: 90vh;
      border-radius: 8px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.25);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      pointer-events: auto;
    }
    .group-overlay__header {
      padding: 12px 16px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f8fafc;
    }
    .group-overlay__body {
      padding: 12px 16px;
      overflow: auto;
      flex: 1;
    }
    .group-overlay__close {
      border: none;
      background: transparent;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      color: #64748b;
    }
    .action-overlay {
      position: fixed;
      inset: 0;
      z-index: 1060;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .action-overlay__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      pointer-events: auto;
    }
    .action-overlay__panel {
      position: relative;
      background: #fff;
      width: 520px;
      max-width: 95vw;
      border-radius: 10px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.25);
      display: flex;
      flex-direction: column;
      overflow: visible;
      pointer-events: auto;
      max-height: 90vh;
    }
    .action-overlay__header {
      padding: 12px 16px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f8fafc;
      font-weight: 600;
    }
    .action-overlay__body {
      padding: 14px 16px;
      overflow: visible;
    }
    .action-overlay__close {
      border: none;
      background: transparent;
      font-size: 1.25rem;
      line-height: 1;
      cursor: pointer;
      color: #64748b;
    }
    .actions-col-sticky {
      position: sticky;
      left: 0;
      background: #fff;
      z-index: 5;
    }
    .actions-menu {
      z-index: 6000 !important;
      position: absolute !important;
      transform: translate3d(0, 0, 0);
    }
    .actions-row {
      position: relative;
      z-index: 1;
      overflow: visible;
    }
    .actions-row.actions-row-active {
      z-index: 20;
    }
  `;

  const toggleUser = (user) => {
    setExpanded((prev) => {
      const nextVal = !prev[user.email];
      // If we're expanding for the first time, load details
      if (nextVal && (!details[user.email] || !details[user.email].loaded)) {
        loadUserDetails(user);
      }
      return { ...prev, [user.email]: nextVal };
    });
  };

  const loadUserDetails = async (user) => {
    const { t1, t2 } = buildDefaultRange();
    const email = user.email;
    setDetails((prev) => ({
      ...prev,
      [email]: { ...(prev[email] || {}), loading: true, error: null },
    }));

    try {
      const [configs, usage] = await Promise.all([
        apiClient.adminGetUserConfigs(email),
        apiClient.adminDetailUsage(
          t1.toISOString(),
          t2.toISOString(),
          [email],
          false,
        ),
      ]);

      setDetails((prev) => ({
        ...prev,
        [email]: {
          ...(prev[email] || {}),
          loading: false,
          loaded: true,
          configs: Array.isArray(configs) ? configs : [],
          usage,
          range: { t1, t2 },
        },
      }));
    } catch (err) {
      console.error('Error loading user details:', err);
      setDetails((prev) => ({
        ...prev,
        [email]: {
          ...(prev[email] || {}),
          loading: false,
          loaded: true,
          error: 'Failed to load configs or usage.',
        },
      }));
    }
  };

  const openGroupUsage = async (groupId) => {
    if (!groupId) return;
    const { t1, t2 } = buildDefaultRange();
    setGroupUsage({
      isOpen: true,
      loading: true,
      data: null,
      groupId,
      error: null,
      range: { t1, t2 },
    });
    setGroupSort({ key: 'cost', dir: 'desc' });
    try {
      const data = await apiClient.adminDetailUsage(
        t1.toISOString(),
        t2.toISOString(),
        [String(groupId)],
        true,
      );
      setGroupUsage((prev) => ({
        ...prev,
        loading: false,
        data,
      }));
    } catch (err) {
      console.error('Error loading group usage:', err);
      setGroupUsage((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load group usage.',
      }));
    }
  };

  const closeGroupUsage = () =>
    setGroupUsage({
      isOpen: false,
      loading: false,
      data: null,
      groupId: null,
      error: null,
      range: null,
    });

  const openActionModal = (user, action) =>
    setActionModal({ open: true, user, action });
  useEffect(() => {
    if (actionModal.open) {
      const replayToken =
        actionModal.action === 'GMode'
          ? readStoredCode(apiClient.sessionEmail, 12 * 60 * 60 * 1000)
          : null;
      setActionForm({
        expirationDate: new Date(),
        mfaCode: '',
        replayToken,
        submitting: false,
        status: null,
      });
    }
  }, [actionModal.open, actionModal.action, apiClient.sessionEmail]);
  const closeActionModal = () =>
    setActionModal({ open: false, user: null, action: null });

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (actionModal.action !== 'Set Expiration') return;
    const email = actionModal.user?.email;
    if (!email || !actionForm.expirationDate) {
      setActionForm((prev) => ({ ...prev, status: 'Missing email or date.' }));
      return;
    }
    setActionForm((prev) => ({ ...prev, submitting: true, status: null }));
    try {
      const iso = actionForm.expirationDate.toISOString().slice(0, -1);
      const rsp = await apiClient.adminSetExpiration(email, '', iso, true);
      setActionForm((prev) => ({
        ...prev,
        submitting: false,
      status: rsp?.Status || 'Expiration updated.',
      }));
    closeActionModal();
    } catch (err) {
      console.error(err);
      setActionForm((prev) => ({
        ...prev,
        submitting: false,
        status: 'Error updating expiration.',
      }));
    }
  };

  const handleGModeSubmit = async (e) => {
    e.preventDefault();
    const email = actionModal.user?.email;
    const adminEmail = apiClient.sessionEmail;
    if (!email || (!actionForm.mfaCode && !actionForm.replayToken)) {
      setActionForm((prev) => ({ ...prev, status: 'Missing MFA code.' }));
      return;
    }
    setActionForm((prev) => ({ ...prev, submitting: true, status: null }));
    try {
      const rsp = await apiClient.adminImpersonateUser(
        actionForm.mfaCode,
        email,
        actionForm.replayToken,
      );
      if (rsp?.sessionKey && rsp?.sessionEmail) {
        if (rsp.replayToken) {
          writeStoredCode(adminEmail, rsp.replayToken);
        }
        // Set out local session key and email to the new ones...
        cacheManager.clear();
        apiClient.reset();
        apiClient.setSessionKey(rsp.sessionKey);
        apiClient.setSessionEmail(rsp.sessionEmail);

        setActionForm((prev) => ({
          ...prev,
          submitting: false,
          status: 'Impersonated user.',
        }));
        closeActionModal();
        
        navigate('/');

      } else if (
        rsp?.replayMfaRequired ||
        (rsp?.error === 'Invalid MFA code' &&
          actionForm.replayToken &&
          !actionForm.mfaCode)
      ) {
        clearStoredCode();
        setActionForm((prev) => ({
          ...prev,
          replayToken: null,
          mfaCode: '',
          submitting: false,
          status: 'Enter your authenticator code to continue.',
        }));
      } else if (rsp?.error) {
        setActionForm((prev) => ({
          ...prev,
          submitting: false,
          status: rsp.error,
        }));
      } else {
        setActionForm((prev) => ({
          ...prev,
          submitting: false,
          status: `Unexpected response: ${JSON.stringify(rsp)}`,
        }));
      }
    } catch (err) {
      console.error(err);
      setActionForm((prev) => ({
        ...prev,
        submitting: false,
        status: `Error impersonating user: ${JSON.stringify(
          err?.response?.data || err?.message || err,
        )}`,
      }));
    }
  };

  const normalizeBackendFeatures = (raw) => {
    const source = raw && raw.features ? raw.features : raw || {};
    const normalized = {};
    Object.entries(source).forEach(([name, val]) => {
      normalized[name] = Array.isArray(val) ? val : [val];
    });
    return normalized;
  };

  const normalizeUserFeatures = (raw) => {
    if (!raw) return {};
    if (raw.user_features && typeof raw.user_features === 'object') {
      return raw.user_features;
    }
    if (typeof raw === 'object') {
      return raw;
    }
    return {};
  };

  const combineFeatures = (backend, user) => {
    if (!backend || Object.keys(backend).length === 0) return {};
    const combined = {};
    Object.keys(backend).forEach((featureName) => {
      const options = backend[featureName] || [];
      const rawCurrent =
        featureName in user ? user[featureName] : undefined;
      const matched = options.find((o) => String(o) === String(rawCurrent));
      combined[featureName] = { options, currentValue: matched };
    });
    return combined;
  };

  useEffect(() => {
    if (actionModal.open && actionModal.action === 'Feature Management') {
      const email = actionModal.user?.email;
      if (!email) return;
      setActionFeatures((prev) => ({
        ...prev,
        isLoading: true,
        status: 'Loading features...',
      }));
      Promise.all([
        apiClient.adminGetBackendFeatures(),
        apiClient.adminGetUsersFeatures(email),
      ])
        .then(([backend, userRaw]) => {
          const backendMerged = { ...(backend || {}), ...FRONTEND_FEATURES };
          const backendNorm = normalizeBackendFeatures(backendMerged);
          const userNorm = normalizeUserFeatures(userRaw);
          setActionFeatures({
            backend: backendNorm,
            user: userNorm,
            combined: combineFeatures(backendNorm, userNorm),
            isLoading: false,
            status: 'Features loaded.',
          });
        })
        .catch((error) => {
          console.error('Error loading feature data:', error);
          setActionFeatures((prev) => ({
            ...prev,
            isLoading: false,
            status: 'Error loading feature data.',
          }));
        });
    }
  }, [actionModal.open, actionModal.action, actionModal.user, apiClient]);

  const handleInlineSetFeature = async (featureName, newValue) => {
    const email = actionModal.user?.email;
    if (!email) return;
    setActionFeatures((prev) => ({
      ...prev,
      status: `Setting ${featureName}...`,
    }));
    setActionFeatures((prev) => ({
      ...prev,
      combined: {
        ...prev.combined,
        [featureName]: {
          ...prev.combined[featureName],
          currentValue: newValue,
        },
      },
    }));
    try {
      await apiClient.adminSetFeature([email], featureName, newValue);
      setActionFeatures((prev) => ({
        ...prev,
        status: `Set ${featureName} to ${newValue}`,
      }));
    } catch (err) {
      console.error(err);
      setActionFeatures((prev) => ({
        ...prev,
        status: `Error setting ${featureName}`,
      }));
    }
  };

  const renderUsageForUser = (email) => {
    const entry =
      details[email]?.usage?.users && details[email].usage.users[email];
    const total = details[email]?.usage;

    if (details[email]?.usage?.error) {
      return <div className="text-muted">{details[email].usage.error}</div>;
    }

    if (!entry || !total) {
      return <div className="text-muted">No usage data found.</div>;
    }

    return (
      <Table
        size="sm"
        responsive
        className="mb-2"
        style={{ fontSize: '0.85rem' }}
      >
        <thead>
          <tr>
            <th>Visits</th>
            <th>Hours</th>
            <th>Input Tokens</th>
            <th>Output Tokens</th>
            <th>Cost ($)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{entry.visits.length.toLocaleString()}</td>
            <td>{formatHours(entry.seconds)}</td>
            <td>{entry.input_tokens.toLocaleString()}</td>
            <td>{entry.output_tokens.toLocaleString()}</td>
            <td>{formatCost(entry.cost)}</td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const renderConfigs = (email) => {
    const configs = details[email]?.configs || [];
    if (configs.length === 0) {
      return <div className="text-muted">No configs found.</div>;
    }
    return (
      <Table
        size="sm"
        responsive
        className="mb-2"
        style={{ fontSize: '0.85rem' }}
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>ID</th>
            <th>EMR URL</th>
            <th>Updated</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((cfg) => (
            <tr key={cfg.id}>
              <td>{cfg.name}</td>
              <td>{cfg.id}</td>
              <td>{cfg.emr_url || '-'}</td>
                                  <td>
                                    {formatDateShort(cfg.updated_at) || '-'}
                                  </td>
                                  <td>
                                    {formatDateShort(cfg.created_at) || '-'}
                                  </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const groupSummary = useMemo(() => {
    if (!groupUsage.data || !groupUsage.data.users) return null;
    const total = groupUsage.data;

    const sortedUsers = Object.entries(groupUsage.data.users || {}).sort(
      ([emailA, a], [emailB, b]) => {
        const { key, dir } = groupSort;
        const factor = dir === 'desc' ? -1 : 1;

        const getVal = (entry) => {
          if (!entry) return '';
          if (key === 'email') return entry.email || '';
          if (key === 'visits') return entry.visits?.length || 0;
          const raw =
            key === 'cost'
              ? entry.cost
              : key === 'hours'
                ? entry.seconds
                : key === 'input_tokens'
                  ? entry.input_tokens
                  : key === 'output_tokens'
                    ? entry.output_tokens
                    : key === 'last_modified'
                      ? entry.updated_at || entry.last_modified
                      : key === 'created_at'
                        ? entry.created_at
                        : entry[key];
          if (raw === undefined || raw === null) return '';
          if (typeof raw === 'number') return raw;
          if (typeof raw === 'string') return raw.toLowerCase();
          return raw;
        };

        const va = getVal(a);
        const vb = getVal(b);
        if (va < vb) return -1 * factor;
        if (va > vb) return 1 * factor;
        return emailA.localeCompare(emailB) * factor;
      },
    );

    return { users: sortedUsers, total };
  }, [groupUsage, groupSort]);

  return (
    <>
      <style>{modalSizingStyles}</style>
      <Card className="mb-2">
        <CardHeader style={{ padding: '0.6rem 0.75rem' }}>
          <h6 className="mb-0">Dev Master Dashboard</h6>
        </CardHeader>
        <CardBody style={{ padding: '0.75rem' }}>
          <Form onSubmit={handleSearch} style={{ marginBottom: 0 }}>
            <FormGroup row className="mb-2">
              <Label for="searchKey" sm={2}>
                Search Users
              </Label>
              <Col sm={8}>
                <Input
                  id="searchKey"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder="Name or email"
                />
              </Col>
              <Col sm={2} className="text-right">
                <Button color="primary" type="submit" disabled={searching}>
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </Col>
            </FormGroup>
            {searchError && (
              <div className="text-danger" style={{ marginTop: '-0.25rem' }}>
                {searchError}
              </div>
            )}
          </Form>
        </CardBody>
      </Card>

          {results.length === 0 && !searching ? (
        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
          No users to show. Run a search.
        </div>
      ) : (
        <>
          {results.length > 0 && (
            <Card className="mb-2">
              <CardHeader style={{ padding: '0.6rem 0.75rem' }}>
                <Row className="align-items-center">
                  <Col md="auto" className="d-flex align-items-center">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        color="link"
                        className="p-0 mr-2"
                        style={{ fontSize: '1.1rem', lineHeight: 1 }}
                      >
                        ⚙
                      </DropdownToggle>
                      <DropdownMenu
                        right
                        style={{ minWidth: '280px', padding: '0.75rem' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem',
                            fontWeight: 600,
                          }}
                        >
                          <span>Columns</span>
                          <Button
                            size="sm"
                            color="link"
                            onClick={() => {
                              setColumnSearch('');
                              persistColumns(allKeys);
                            }}
                          >
                            Select all
                          </Button>
                        </div>
                        <Input
                          bsSize="sm"
                          placeholder="Search columns..."
                          value={columnSearch}
                          onChange={(e) => setColumnSearch(e.target.value)}
                          className="mb-2"
                        />
                        <div
                          style={{
                            maxHeight: '260px',
                            overflowY: 'auto',
                            paddingRight: '0.25rem',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px',
                            padding: '6px',
                            background: '#fafafa',
                          }}
                        >
                          {filteredKeys.length === 0 && (
                            <div
                              className="text-muted"
                              style={{ fontSize: '0.85rem' }}
                            >
                              No columns match.
                            </div>
                          )}
                          {filteredKeys.map((key) => {
                            const selected = visibleColumns.includes(key);
                            const idx = visibleColumns.indexOf(key);
                            const canUp = selected && idx > 0;
                            const canDown =
                              selected && idx < visibleColumns.length - 1;
                            return (
                              <div
                                key={key}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 4px',
                                  fontSize: '0.9rem',
                                  backgroundColor: selected ? '#e8f4ff' : 'white',
                                  border:
                                    idx === -1
                                      ? '1px solid transparent'
                                      : '1px solid #d0e3ff',
                                  borderRadius: '4px',
                                  marginBottom: '4px',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleColumn(key)}
                                  style={{ margin: 0 }}
                                />
                                <span
                                  style={{ flex: 1, cursor: 'pointer' }}
                                  onClick={() => toggleColumn(key)}
                                >
                                  {key}
                                  {selected && (
                                    <span className="text-muted" style={{ marginLeft: 6, fontSize: '0.75rem' }}>
                                      (pos {idx + 1})
                                    </span>
                                  )}
                                </span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <Button
                                    size="sm"
                                    color="link"
                                    disabled={!canUp}
                                    onClick={() => moveColumn(key, -1)}
                                    style={{ padding: '0 6px' }}
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="link"
                                    disabled={!canDown}
                                    onClick={() => moveColumn(key, 1)}
                                    style={{ padding: '0 6px' }}
                                  >
                                    ↓
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div
                          className="d-flex justify-content-between"
                          style={{ marginTop: '0.5rem' }}
                        >
                          <Button
                            size="sm"
                            color="secondary"
                            onClick={() => persistColumns([])}
                          >
                            Clear all
                          </Button>
                          <Button
                            size="sm"
                            color="primary"
                            onClick={() => persistColumns(filteredKeys)}
                          >
                            Select filtered
                          </Button>
                        </div>
                        <div style={{ marginTop: '0.75rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '0.35rem',
                              fontWeight: 600,
                            }}
                          >
                            <span>Pinned fields</span>
                            <Button
                              size="sm"
                              color="link"
                              onClick={() => persistPins([])}
                            >
                              Clear
                            </Button>
                          </div>
                          <div
                            style={{
                              maxHeight: '140px',
                              overflowY: 'auto',
                              border: '1px solid #e9ecef',
                              borderRadius: '6px',
                              padding: '6px',
                              background: '#fafafa',
                            }}
                          >
                            {orderedKeys.length === 0 && (
                              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                No fields available.
                              </div>
                            )}
                            {orderedKeys.map((key) => (
                              <label
                                key={`pin-${key}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '4px 0',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={pinnedFields.includes(key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      persistPins([...pinnedFields, key]);
                                    } else {
                                      persistPins(pinnedFields.filter((k) => k !== key));
                                    }
                                  }}
                                  style={{ margin: 0 }}
                                />
                                <span>{key}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    <h6 className="mb-0">User Overview</h6>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody
                style={{
                  padding: '0.75rem',
                  minHeight: '280px',
                  overflow: 'visible',
                }}
              >
                <div
                  style={{
                    overflowX: 'auto',
                    overflowY: 'visible',
                    position: 'relative',
                    paddingBottom: '240px',
                  }}
                >
                  <Table size="sm" responsive style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th className="actions-col-sticky">
                          Actions
                        </th>
                        {visibleColumns.map((col) => (
                          <th
                            key={col}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={() =>
                              setSortState((prev) => ({
                                key: col,
                                dir:
                                  prev.key === col && prev.dir === 'asc'
                                    ? 'desc'
                                    : 'asc',
                              }))
                            }
                          >
                            {col}
                            {sortState.key === col
                              ? sortState.dir === 'asc'
                                ? ' ↑'
                                : ' ↓'
                              : ''}
                          </th>
                        ))}
                        <th style={{ whiteSpace: 'nowrap' }}>Expand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleColumns.length === 0 ? (
                        <tr>
                          <td colSpan={(visibleColumns.length || 0) + 2} className="text-muted">
                            Select columns using the gear to view data.
                          </td>
                        </tr>
                      ) : (
                        sortedResults.map((user) => {
                          const isOpen = expanded[user.email];
                          return (
                            <React.Fragment key={user.email || user.name}>
                              <tr
                                className={`actions-row ${openActionRow === user.email ? 'actions-row-active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleUser(user)}
                              >
                                <td className="actions-col-sticky">
                                  <UncontrolledDropdown
                                    onToggle={(isOpen) =>
                                      setOpenActionRow(isOpen ? user.email : null)
                                    }
                                  >
                                    <DropdownToggle
                                      color="link"
                                      className="p-0"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        background: '#f8fafc',
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      ⋮
                                    </DropdownToggle>
                                    <DropdownMenu
                                      right
                                      className="actions-menu"
                                      container="body"
                                      style={{
                                        minWidth: '220px',
                                        minHeight: '180px',
                                        overflowY: 'auto',
                                      }}
                                    >
                                      {[
                                        'GMode',
                                        'Feature Management',
                                        'Set Expiration',
                                      ].map((action) => (
                                        <DropdownItem
                                          key={action}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openActionModal(user, action);
                                          }}
                                        >
                                          {action}
                                        </DropdownItem>
                                      ))}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                                {visibleColumns.map((col) => (
                                  <td key={col}>{displayValue(user[col])}</td>
                                ))}
                                <td>
                                  <Button
                                    color="link"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleUser(user);
                                    }}
                                  >
                                    {isOpen ? 'Hide' : 'Show'}
                                  </Button>
                                </td>
                              </tr>
                              {isOpen && (
                                <tr>
                                  <td
                                    colSpan={visibleColumns.length + 1}
                                    style={{ background: '#f8fafc' }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <div style={{ fontWeight: 600 }}>
                                        {user.name || 'Unnamed User'} ({user.email})
                                      </div>
                                      {user.group_subscription_id && (
                                        <Button
                                          color="link"
                                          size="sm"
                                          onClick={() =>
                                            openGroupUsage(user.group_subscription_id)
                                          }
                                        >
                                          Group #{user.group_subscription_id}
                                        </Button>
                                      )}
                                    </div>
                                    {details[user.email]?.loading && (
                                      <div className="d-flex align-items-center mb-2">
                                        <Spinner size="sm" className="mr-2" />
                                        <span>Loading configs & usage...</span>
                                      </div>
                                    )}
                                    {details[user.email]?.error && (
                                      <div className="text-danger mb-2">
                                        {details[user.email].error}
                                      </div>
                                    )}
                                    {renderUserFields(user)}
                                    <div className="mb-3" style={{ marginTop: '0.5rem' }}>
                                      {renderPinnedFields(user)}
                                    </div>
                                    <div className="mb-3">
                                      <h6 style={{ fontSize: '0.95rem' }}>
                                        Usage (last 30 days)
                                      </h6>
                                      {renderUsageForUser(user.email)}
                                    </div>
                                    <div>
                                      <h6 style={{ fontSize: '0.95rem' }}>Configs</h6>
                                      {renderConfigs(user.email)}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          )}

        </>
      )}

      {groupUsage.isOpen && (
        <div className="group-overlay">
          <div className="group-overlay__backdrop" onClick={closeGroupUsage} />
          <div className="group-overlay__panel">
            <div className="group-overlay__header">
              <div>
                Group Usage {groupUsage.groupId ? `#${groupUsage.groupId}` : ''}
              </div>
              <button
                className="group-overlay__close"
                onClick={closeGroupUsage}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="group-overlay__body">
              {groupUsage.loading && (
                <div className="d-flex align-items-center mb-2">
                  <Spinner size="sm" className="mr-2" /> Loading group usage...
                </div>
              )}
              {groupUsage.error && (
                <div className="text-danger mb-2">{groupUsage.error}</div>
              )}
              {groupSummary && (
                <>
                  <div className="mb-2">
                    <strong>Total Visits:</strong>{' '}
                    {groupSummary.total.totalVisits.toLocaleString()}
                    {' | '}
                    <strong>Total Hours:</strong>{' '}
                    {groupSummary.total.totalHoursTranscribed.toLocaleString()}
                    {' | '}
                    <strong>Total Cost:</strong>{' '}
                    {formatCost(groupSummary.total.totalCost)}
                  </div>
                  <Table size="sm" responsive style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        {[
                          ['email', 'Email'],
                          ['visits', 'Visits'],
                          ['hours', 'Hours'],
                          ['input_tokens', 'Input Tokens'],
                          ['output_tokens', 'Output Tokens'],
                          ['cost', 'Cost ($)'],
                          ['last_modified', 'Last Modified'],
                          ['created_at', 'Created At'],
                        ].map(([key, label]) => (
                          <th
                            key={key}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                            onClick={() =>
                              setGroupSort((prev) => ({
                                key,
                                dir:
                                  prev.key === key && prev.dir === 'asc'
                                    ? 'desc'
                                    : 'asc',
                              }))
                            }
                          >
                            {label}
                            {groupSort.key === key
                              ? groupSort.dir === 'asc'
                                ? ' ↑'
                                : ' ↓'
                              : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groupSummary.users.map(([email, entry]) => (
                        <tr key={email}>
                          <td>{email}</td>
                          <td>{entry.visits.length.toLocaleString()}</td>
                          <td>{formatHours(entry.seconds)}</td>
                          <td>{entry.input_tokens.toLocaleString()}</td>
                          <td>{entry.output_tokens.toLocaleString()}</td>
                          <td>{formatCost(entry.cost)}</td>
                          <td>
                            {formatDateShort(entry.updated_at || entry.last_modified) ||
                              '-'}
                          </td>
                          <td>{formatDateShort(entry.created_at) || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {actionModal.open && (
        <div className="action-overlay">
          <div className="action-overlay__backdrop" onClick={closeActionModal} />
          <div className="action-overlay__panel">
            <div className="action-overlay__header">
              <div>Action: {actionModal.action}</div>
              <button
                className="action-overlay__close"
                onClick={closeActionModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="action-overlay__body">
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>User:</strong> {actionModal.user?.email || 'Unknown'}
              </div>
              {actionModal.action === 'Set Expiration' ? (
                <Form onSubmit={handleActionSubmit}>
                  <FormGroup>
                    <Label>Expiration Date</Label>
                    <div>
                      <DatePicker
                        selected={actionForm.expirationDate}
                        onChange={(date) =>
                          setActionForm((prev) => ({ ...prev, expirationDate: date }))
                        }
                        showTimeSelect
                        timeIntervals={15}
                        dateFormat="yyyy-MM-dd h:mm aa"
                        className="form-control"
                      />
                    </div>
                  </FormGroup>
                  {actionForm.status && (
                    <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                      {actionForm.status}
                    </div>
                  )}
                  <div className="d-flex justify-content-end" style={{ marginTop: '1rem' }}>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={closeActionModal}
                      disabled={actionForm.submitting}
                      className="mr-2"
                    >
                      Cancel
                    </Button>
                    <Button color="primary" size="sm" type="submit" disabled={actionForm.submitting}>
                      {actionForm.submitting ? 'Saving...' : 'Set Expiration'}
                    </Button>
                  </div>
                </Form>
              ) : actionModal.action === 'GMode' ? (
                <Form onSubmit={handleGModeSubmit}>
                  {actionForm.replayToken ? (
                    <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                      MFA verified for 12 hours.
                    </div>
                  ) : (
                    <FormGroup>
                      <Label>MFA Code</Label>
                      <Input
                        type="text"
                        value={actionForm.mfaCode}
                        onChange={(e) =>
                          setActionForm((prev) => ({ ...prev, mfaCode: e.target.value }))
                        }
                        placeholder="Enter MFA code"
                      />
                    </FormGroup>
                  )}
                  {actionForm.status && (
                    <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                      {actionForm.status}
                    </div>
                  )}
                  <div className="d-flex justify-content-end" style={{ marginTop: '1rem' }}>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={closeActionModal}
                      disabled={actionForm.submitting}
                      className="mr-2"
                    >
                      Cancel
                    </Button>
                    <Button color="primary" size="sm" type="submit" disabled={actionForm.submitting}>
                      {actionForm.submitting ? 'Impersonating...' : 'Impersonate'}
                    </Button>
                  </div>
                </Form>
              ) : actionModal.action === 'Feature Management' ? (
                <>
                  <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                    Manage feature flags for this user.
                  </div>
                  {actionFeatures.isLoading && (
                    <div className="d-flex align-items-center mb-2">
                      <Spinner size="sm" className="mr-2" /> Loading features...
                    </div>
                  )}
                  {actionFeatures.status && (
                    <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                      {actionFeatures.status}
                    </div>
                  )}
                  <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {Object.keys(actionFeatures.combined).length === 0 && !actionFeatures.isLoading && (
                      <div className="text-muted">No features available.</div>
                    )}
                    {Object.entries(actionFeatures.combined).map(
                      ([featureName, featureData]) => (
                        <div
                          key={featureName}
                          style={{
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <Label
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              minWidth: '140px',
                            }}
                          >
                            {featureName}
                            <FeatureInfo featureName={featureName} idPrefix="dm" />
                          </Label>
                          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {(featureData.options && featureData.options.length > 0
                              ? featureData.options
                              : ['enabled']
                            ).map((option, idx) => {
                              const isSingle =
                                !featureData.options || featureData.options.length <= 1;
                              const isSelected =
                                featureData.currentValue === undefined
                                  ? false
                                  : isSingle
                                    ? true
                                    : String(featureData.currentValue) === String(option);
                              const radiusLeft = idx === 0 ? '6px' : '0px';
                              const radiusRight =
                                idx === (featureData.options || ['enabled']).length - 1
                                  ? '6px'
                                  : '0px';
                              return (
                                <button
                                  key={String(option)}
                                  type="button"
                                  onClick={() => {
                                    if (!isSingle) handleInlineSetFeature(featureName, option);
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    border: `1px solid #6bd888`,
                                    borderLeftWidth: idx === 0 ? '1px' : '0px',
                                    backgroundColor: isSelected ? '#6bd888' : '#ffffff',
                                    color: isSelected ? '#ffffff' : '#1f2937',
                                    cursor: isSingle ? 'default' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: isSelected ? 600 : 500,
                                    lineHeight: '20px',
                                    borderTopLeftRadius: radiusLeft,
                                    borderBottomLeftRadius: radiusLeft,
                                    borderTopRightRadius: radiusRight,
                                    borderBottomRightRadius: radiusRight,
                                    margin: 0,
                                    whiteSpace: 'nowrap',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected && !isSingle)
                                      e.currentTarget.style.backgroundColor = '#f4fff8';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected && !isSingle)
                                      e.currentTarget.style.backgroundColor = '#ffffff';
                                  }}
                                >
                                  {String(option)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                  <div className="d-flex justify-content-end" style={{ marginTop: '1rem' }}>
                    <Button color="secondary" size="sm" onClick={closeActionModal}>
                      Close
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-muted" style={{ fontSize: '0.95rem' }}>
                  This would open the {actionModal.action} flow. Implement the desired
                  modal content here.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DevMasterDashboard;
